// Theme manager for the Dark Side / Light Side toggle.
//
// The theme is a body class ("light") driven by CSS custom properties in
// theme.css. Components read colors via var(...) so flipping the class
// restyles everything without a re-render. Data-driven colors (per-set
// parallel hexes, state accents) can't use var() where they get alpha
// suffixes concatenated (e.g. accent + '22'), so those go through
// lightAccent() / the themed constants below, resolved per render — the
// toggle lives on the profile page, so the checklist always re-renders
// after a change.

const THEME_KEY = 'ck_theme';

export const isLight = () =>
  typeof document !== 'undefined' && document.body.classList.contains('light');

export const getTheme = () => (isLight() ? 'light' : 'dark');

export function setTheme(theme) {
  try { localStorage.setItem(THEME_KEY, theme); } catch (e) { /* private mode */ }
  document.body.classList.toggle('light', theme === 'light');
}

export function initTheme() {
  let saved = null;
  try { saved = localStorage.getItem(THEME_KEY); } catch (e) { /* private mode */ }
  document.body.classList.toggle('light', saved === 'light');
}

// ---- Light-mode accent mapping -------------------------------------------
// Known dark-theme accents map to hand-tuned WCAG AA equivalents (>=4.5:1 on
// white, same hue). Unknown colors — parallel hexes from any of the 45 set
// files — are darkened algorithmically to the same 4.5:1 target and cached.

const LIGHT_ACCENT_MAP = {
  '#00d4ff': '#007fb0', // cyan
  '#00e87a': '#00884a', // green
  '#f0c040': '#987009', // gold / superfractor
  '#b060ff': '#8a3ae0', // purple
  '#00e5ff': '#008299', // ice / aqua
  '#c9a227': '#8f6f14', // brand gold
  '#ff8c42': '#bb5b1a', // sketch orange
  '#ff3355': '#c9102f', // error red
  '#ff1744': '#e9153e', // red refractor
  '#c8d8f0': '#6b778f', // refractor silver
  '#b0e0ff': '#4a7b9b', // mini-diamond
  '#00d4cc': '#00847f', // aqua sonar
  '#00bcd4': '#008392', // aqua lava
  '#2979ff': '#2670ec', // blue
  '#00b0ff': '#007dbc', // wave
  '#e040fb': '#bd36d4', // prism
  '#ff6d00': '#c55400', // mojo
  '#ff9100': '#af6300', // orange
  '#76ff03': '#3e8600', // x-fractor
};

function channelLum(v) {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function contrastOnWhite(r, g, b) {
  const lum = 0.2126 * channelLum(r) + 0.7152 * channelLum(g) + 0.0722 * channelLum(b);
  return 1.05 / (lum + 0.05);
}

function darkenToAA(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const rgb = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16));
  if (rgb.some(Number.isNaN)) return hex;
  for (let s = 1.0; s > 0; s -= 0.005) {
    const [r, g, b] = rgb.map(v => Math.round(v * s));
    if (contrastOnWhite(r, g, b) >= 4.5) {
      return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    }
  }
  return '#000000';
}

export function lightAccent(color) {
  if (!isLight() || !color || color[0] !== '#') return color;
  const key = color.toLowerCase();
  if (!LIGHT_ACCENT_MAP[key]) LIGHT_ACCENT_MAP[key] = darkenToAA(key);
  return LIGHT_ACCENT_MAP[key];
}

// Themed plain-hex accents for code paths that concatenate alpha suffixes
// (e.g. color + '22') and therefore can't use var() references.
const ACCENTS_DARK = { cyan: '#00d4ff', green: '#00e87a', gold: '#f0c040', purple: '#b060ff', ice: '#00e5ff' };
const ACCENTS_LIGHT = { cyan: '#007fb0', green: '#00884a', gold: '#987009', purple: '#8a3ae0', ice: '#008299' };

export const getAccents = () => (isLight() ? ACCENTS_LIGHT : ACCENTS_DARK);
